import { ArrowDown, ArrowRight, Clock3, ListOrdered } from "lucide-react";
import { Suspense, lazy, useState } from "react";

import { ButtonLink } from "@/components/button-link";
import { Button } from "@/components/ui/button";

/**
 * What the challenge is, and how long this will take -- beside the maze being mapped.
 *
 * The view brings three.js with it, so it is its own chunk, fetched only here and only when
 * the browser has WebGL. Without WebGL the space stays empty -- the picture is an
 * illustration, not information.
 */

const MapReveal3D = lazy(() =>
  import("@/components/guide/map-reveal-3d").then((m) => ({ default: m.MapReveal3D })),
);

function hasWebGL(): boolean {
  if (typeof document === "undefined") return false;
  try {
    return document.createElement("canvas").getContext("webgl2") !== null;
  } catch {
    return false;
  }
}

export function Hero({ chapters, minutes, first }: { chapters: number; minutes: number; first: string }) {
  const [webgl] = useState(hasWebGL);
  return (
    <section className="grid items-center gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" aria-labelledby="guide-title">
      <div className="min-w-0 space-y-5">
        <p className="text-primary text-sm font-medium">Visual Navigation Challenge</p>
        <h1 id="guide-title" className="text-3xl font-semibold tracking-tight sm:text-4xl">
          Navigate a maze by camera alone.
        </h1>
        <p className="text-muted-foreground text-base leading-7">
          A robot is placed in a maze it has explored before. Your code gets its camera frames and
          four photos of the goal, picks each move, and checks in when it thinks it has arrived.
          Runs are scored on distance to the goal, heading and path length.
        </p>
        <dl className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div className="flex items-center gap-1.5">
            <ListOrdered className="size-4" aria-hidden />
            <dt className="sr-only">Chapters</dt>
            <dd>{chapters} chapters</dd>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock3 className="size-4" aria-hidden />
            <dt className="sr-only">Time</dt>
            <dd>about {minutes} minutes</dd>
          </div>
        </dl>
        <div className="flex flex-wrap gap-2">
          <Button nativeButton={false} render={<a href={`#${first}`} />}>
            Start step by step
            <ArrowDown data-icon="inline-end" />
          </Button>
          <ButtonLink to="/challenges" variant="outline">
            Go to challenges
            <ArrowRight data-icon="inline-end" />
          </ButtonLink>
        </div>
      </div>
      <div className="min-w-0">
        {webgl ? (
          <Suspense fallback={<div className="bg-muted/40 aspect-[4/3] w-full rounded-lg border" aria-hidden />}>
            <MapReveal3D className="bg-muted/40 border" />
          </Suspense>
        ) : null}
      </div>
    </section>
  );
}
